import React, {useState, useEffect, useRef} from 'react'
import PubSub from '../Broker'
import '../../../styles/index.scss'
import {useSelectedVariantContext} from '../context/SelectedVariantContext'
import {DESIGN_MODES} from '../Step2Container'
import {COVERAGE_EVENTS} from '../coverage-events'

// Module-level cache — updated silently in the background as worker results arrive.
// Keyed by side name (e.g. 'front', 'back', 'all-in-one').
// Persists across layout mode switches so warnings are restored when switching back.
let _lastWarnings = {}

// Top banner warning when artwork doesn't fully cover the safezone print area.
// Warnings are shown only when the user clicks Next (ARTWORK_COVERAGE_BLOCK_NEXT).
// Coverage checks run silently in the background — results are cached but not displayed immediately.
const ArtworkCoverageWarning = () => {
    const {state: {designMode}} = useSelectedVariantContext()
    const [visibleWarnings, setVisibleWarnings] = useState({})
    const [dismissed, setDismissed] = useState(false)
    const mountedRef = useRef(true)

    // Reset dismissed state when switching layout mode so restored warnings are visible.
    const prevDesignModeRef = useRef(designMode)
    useEffect(() => {
        if (prevDesignModeRef.current !== designMode) {
            prevDesignModeRef.current = designMode
            setDismissed(false)
        }
    }, [designMode])

    useEffect(() => {
        mountedRef.current = true

        // Update cache silently — never show warning immediately.
        // If a side that was already visible (shown after Next click) now passes, clear it.
        const coverageToken = PubSub.subscribe(COVERAGE_EVENTS.CHECK, (data) => {
            const {side, result} = data || {}
            if (side && result) {
                _lastWarnings = {..._lastWarnings, [side]: result}
                if (!mountedRef.current) return
                if (result.ok) {
                    // Side passed — clear from visible banner if it was previously shown
                    setVisibleWarnings((prev) => {
                        if (!(side in prev)) return prev
                        const next = {...prev}
                        delete next[side]
                        return next
                    })
                }
                // Coverage failed → cache only; warning shown on Next click via ARTWORK_COVERAGE_BLOCK_NEXT
            }
        })

        // BG is global — when active it covers ALL sides, clear every cached warning at once.
        const bgActiveToken = PubSub.subscribe(COVERAGE_EVENTS.BG_ACTIVE, () => {
            _lastWarnings = {}
            if (mountedRef.current) {
                setVisibleWarnings({})
                setDismissed(false)
            }
        })

        // User clicked Next and coverage failed — use authoritative failedSides list from NavBar.
        // NavBar computed this from stageSides (all sides), so it covers unvisited sides too.
        const blockToken = PubSub.subscribe(COVERAGE_EVENTS.BLOCK_NEXT, (data) => {
            if (!mountedRef.current) return
            const {failedSides: blockedSides} = data || {}
            if (!blockedSides || blockedSides.length === 0) return
            // Build a display snapshot: use cached result if available, otherwise mark as failed
            const snapshot = {}
            blockedSides.forEach((side) => {
                snapshot[side] = _lastWarnings[side] || {ok: false}
            })
            setVisibleWarnings(snapshot)
            setDismissed(false)
        })

        // All sides passed — NavBar navigates away and fires this event to clear the banner.
        const clearToken = PubSub.subscribe(COVERAGE_EVENTS.CLEAR, () => {
            _lastWarnings = {}
            if (!mountedRef.current) return
            setVisibleWarnings({})
            setDismissed(false)
        })

        return () => {
            mountedRef.current = false
            PubSub.unsubscribe(coverageToken)
            PubSub.unsubscribe(bgActiveToken)
            PubSub.unsubscribe(blockToken)
            PubSub.unsubscribe(clearToken)
        }
    }, [])

    // Only show sides that belong to the current layout mode:
    // combine → 'all-in-one' only; split → everything except 'all-in-one'.
    const failedSides = Object.entries(visibleWarnings)
        .filter(([side, result]) => {
            if (result.ok) return false
            if (designMode === DESIGN_MODES.COMBINED) return side === 'all-in-one'
            return side !== 'all-in-one'
        })
        .map(([side]) => side)

    if (failedSides.length === 0 || dismissed) return null

    return (
        <div className="ArtworkCoverageWarning">
            <div className="ArtworkCoverageWarning__header">
                <div className="ArtworkCoverageWarning__title">Attention Required</div>
                <button
                    className="ArtworkCoverageWarning__close"
                    onClick={() => setDismissed(true)}
                    aria-label="Dismiss"
                >
                    ✕
                </button>
            </div>
            <div className="ArtworkCoverageWarning__message">
                The design does not fully cover the grey area on the following surface(s): {failedSides.join(', ')}. To avoid printing gaps, please extend your design or apply a background color.
            </div>
        </div>
    )
}

export default ArtworkCoverageWarning
